import axios from 'src/lib/axios';

import { JWT_STORAGE_KEY } from './constant';

// ----------------------------------------------------------------------

const LOGIN_PATH = '/';

export function jwtDecode(token: string) {
  try {
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length < 2) {
      throw new Error('Invalid token!');
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(base64));

    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    throw error;
  }
}

// ----------------------------------------------------------------------

export function isValidToken(accessToken: string) {
  if (!accessToken) {
    return false;
  }

  try {
    const decoded = jwtDecode(accessToken);

    if (!decoded || !('exp' in decoded)) {
      return false;
    }

    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  } catch (error) {
    console.error('Error during token validation:', error);
    return false;
  }
}

// ----------------------------------------------------------------------

let refreshTimeoutId: NodeJS.Timeout | null = null;

export function scheduleTokenRefresh(exp: number, refreshCallback: () => Promise<void>) {
  // Clear any existing refresh timeout
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  const currentTime = Date.now();
  const expirationTime = exp * 1000;
  const timeUntilExpiration = expirationTime - currentTime;

  // Refresh 5 minutes before expiration (300000 ms)
  // If token expires in less than 5 minutes, refresh immediately
  const refreshBuffer = 5 * 60 * 1000;
  const timeUntilRefresh = Math.max(timeUntilExpiration - refreshBuffer, 0);

  console.log(`Token will be refreshed in ${Math.round(timeUntilRefresh / 1000)} seconds`);

  refreshTimeoutId = setTimeout(async () => {
    try {
      console.log('Refreshing access token...');
      await refreshCallback();
    } catch (error) {
      console.error('Failed to refresh token, redirecting to login:', error);
      sessionStorage.removeItem(JWT_STORAGE_KEY);
      window.location.href = LOGIN_PATH;
    }
  }, timeUntilRefresh);
}

// ----------------------------------------------------------------------

export async function setSession(
  accessToken: string | null,
  refreshCallback?: () => Promise<void>
) {
  try {
    if (accessToken) {
      sessionStorage.setItem(JWT_STORAGE_KEY, accessToken);

      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      const decodedToken = jwtDecode(accessToken);

      if (decodedToken && 'exp' in decodedToken) {
        // Schedule automatic token refresh if callback provided
        if (refreshCallback) {
          scheduleTokenRefresh(decodedToken.exp, refreshCallback);
        }
      } else {
        throw new Error('Invalid access token!');
      }
    } else {
      sessionStorage.removeItem(JWT_STORAGE_KEY);
      delete axios.defaults.headers.common.Authorization;

      // Clear any pending refresh timeout
      if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
      }
    }
  } catch (error) {
    console.error('Error during set session:', error);
    throw error;
  }
}
